import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
export function DirectPaymentPage() {
  return <DocPageLayout title="<<directPayment>> Integration" description="Fully control the transaction and manage the payment experience with server-to-server API calls." data-id="element-1521">
      <p className="text-gray-700 mb-6" data-id="element-1522">
        The Direct Payment integration gives you complete control over the
        payment flow. Your server collects payment details and sends them
        directly to the gateway API. This approach offers maximum flexibility
        but requires full PCI DSS compliance since your systems handle sensitive
        card data.
      </p>

      <InfoBox type="warning" title="PCI DSS Compliance Required" data-id="element-1523">
        Direct Payment requires your environment to be PCI DSS Level 1
        compliant, as your servers will process, store, or transmit cardholder
        data. If you are not PCI compliant, consider using Checkout or Hosted
        Session instead.
      </InfoBox>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1524">
        API Endpoint
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1525">
        All Direct Payment requests are sent to the following endpoint:
      </p>
      <CodeBlock language="HTTP" title="Endpoint" code={`PUT https://test-gateway.mastercard.com/api/rest/version/72/merchant/{merchantId}/order/{orderID}/transaction/{transactionId}`} data-id="element-1526" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1527">
        Pay Operation
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1528">
        To process a payment, send a PUT request with the PAY operation. This
        performs both authorization and capture in a single step.
      </p>
      <CodeBlock language="JSON" title="Pay Request" code={`{
  "apiOperation": "PAY",
  "order": {
    "amount": "125.00",
    "currency": "USD"
  },
  "sourceOfFunds": {
    "type": "CARD",
    "provided": {
      "card": {
        "number": "5123450000000008",
        "securityCode": "100",
        "expiry": {
          "month": "05",
          "year": "25"
        }
      }
    }
  }
}`} data-id="element-1529" />

      <CodeBlock language="JSON" title="Successful Response" code={`{
  "result": "SUCCESS",
  "order": {
    "id": "ORDER-67890",
    "status": "CAPTURED",
    "amount": 125.00,
    "currency": "USD",
    "creationTime": "2025-01-15T10:30:00.000Z"
  },
  "transaction": {
    "id": "TXN-001",
    "type": "PAYMENT",
    "result": "SUCCESS",
    "authorizationCode": "012345",
    "receipt": "501234567890"
  },
  "response": {
    "acquirerCode": "00",
    "gatewayCode": "APPROVED"
  }
}`} data-id="element-1530" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1531">
        Required Fields
      </h2>
      <div className="overflow-x-auto my-6" data-id="element-1532">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1533">
          <thead data-id="element-1534">
            <tr className="bg-gray-50" data-id="element-1535">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1536">
                Field
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1537">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1538">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1539">
            <tr data-id="element-1540">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1541">apiOperation</td>
              <td className="px-4 py-3" data-id="element-1542">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1543">
                The operation type: PAY, AUTHORIZE, CAPTURE, REFUND, VOID
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1544">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1545">order.amount</td>
              <td className="px-4 py-3" data-id="element-1546">Decimal</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1547">
                The total amount for the transaction
              </td>
            </tr>
            <tr data-id="element-1548">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1549">order.currency</td>
              <td className="px-4 py-3" data-id="element-1550">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1551">
                ISO 4217 currency code (e.g., USD, EUR, GBP)
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1552">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1553">
                sourceOfFunds.type
              </td>
              <td className="px-4 py-3" data-id="element-1554">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1555">
                Payment source type: CARD, TOKEN, SCHEME_TOKEN
              </td>
            </tr>
            <tr data-id="element-1556">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1557">
                sourceOfFunds.provided.card.number
              </td>
              <td className="px-4 py-3" data-id="element-1558">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1559">The card number (PAN)</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1560">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1561">
                sourceOfFunds.provided.card.securityCode
              </td>
              <td className="px-4 py-3" data-id="element-1562">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1563">The card CVV/CVC</td>
            </tr>
            <tr data-id="element-1564">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1565">
                sourceOfFunds.provided.card.expiry
              </td>
              <td className="px-4 py-3" data-id="element-1566">Object</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1567">
                Card expiry with month and year fields
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1568">
        Error Handling
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1569">
        When a transaction fails, the API returns a detailed error response.
        Always check the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1570">
          result
        </code>{' '}
        field and handle errors gracefully.
      </p>
      <CodeBlock language="JSON" title="Error Response Example" code={`{
  "result": "FAILURE",
  "response": {
    "acquirerCode": "05",
    "gatewayCode": "DECLINED"
  },
  "error": {
    "cause": "INVALID_REQUEST",
    "explanation": "The card was declined by the issuing bank."
  }
}`} data-id="element-1571" />

      <InfoBox type="info" title="Authorize vs Pay" data-id="element-1572">
        Use <strong data-id="element-1573">AUTHORIZE</strong> to reserve funds and{' '}
        <strong data-id="element-1574">CAPTURE</strong> later, or use <strong data-id="element-1575">PAY</strong> to authorize
        and capture in one step. Choose based on your fulfillment workflow.
      </InfoBox>
    </DocPageLayout>;
}